import * as React from 'react';
import { graphql } from 'react-apollo';

import * as Sentry from '@sentry/react';
import { compose } from 'recompose';

import user from 'js/lib/user';
import waitForGraphQL from 'js/lib/waitForGraphQL';

import CalendarSettingsQueryV2 from 'bundles/calendar-sync/queries/CalendarSettingsQueryV2.graphql';
import CreateLearnerCalendarMutation from 'bundles/calendar-sync/queries/CreateLearnerCalendarMutation.graphql';
import type {
  CalendarSettingsQueryV2Query,
  CalendarSettingsQueryV2QueryVariables,
} from 'bundles/calendar-sync/queries/__generated__/CalendarSettingsQueryV2';
import type {
  CalendarMutationMutation,
  CalendarMutationMutationVariables,
} from 'bundles/calendar-sync/queries/__generated__/CreateLearnerCalendarMutation';

type PropsFromCalendarQuery = {
  url?: string;
  data?: {
    refetch?: () => void;
  };
};

type PropsFromCreateCalendarMutation = {
  createCalendarMutation: (_: any) => Promise<any>;
};

type CalendarDataProps = PropsFromCalendarQuery & PropsFromCreateCalendarMutation;
type WithCalendarDataWrapperProps = {
  RenderComponent: React.ComponentType<CalendarDataProps>;
} & CalendarDataProps;

const contextOption = { context: { clientName: 'gatewayGql' } };

export class WithCalendarDataWrapper extends React.PureComponent<WithCalendarDataWrapperProps> {
  componentDidMount() {
    const { createCalendarMutation, data, url } = this.props;
    const userId = user.get()?.id?.toString();
    if (!url && userId) {
      // if there is no calendar, make POST to create calendar
      createCalendarMutation({
        variables: {
          body: {
            userId,
            shouldDisplayContent: true,
          },
        },
        context: contextOption.context,
      }).then(() => {
        data?.refetch?.();
      });
    } else if (!userId) {
      Sentry.captureException('Tried to create a calendar without a user id');
    }
  }

  render() {
    const { RenderComponent, url, ...props } = this.props;

    // The URL passed to us from the server will inherently contain
    // a web protocol (http/https) prefix. In order to append our own
    // (for specialty URLs such as webcal), we must strip out this prefix
    const trimmedUrl = url ? url.replace(/^https?:\/\//g, '') : url;

    let rest = { ...props, calendar: null };

    if (url !== null) {
      const google = `https://calendar.google.com/calendar/r/week?cid=http://${trimmedUrl}`;
      const webcal = `webcal://${trimmedUrl}`;

      rest = {
        ...rest,
        // @ts-expect-error TSMIGRATION
        calendar: {
          google,
          webcal,
          ics: url,
        },
      };
    }

    return <RenderComponent {...rest} />;
  }
}

export const WithCalendarData = (Component: React.ComponentType<CalendarDataProps>) => (props: CalendarDataProps) => (
  <WithCalendarDataWrapper RenderComponent={Component} {...props} />
);

export default compose<{}, PropsFromCalendarQuery & PropsFromCreateCalendarMutation>(
  waitForGraphQL<{}, CalendarSettingsQueryV2Query, CalendarSettingsQueryV2QueryVariables, PropsFromCalendarQuery>(
    CalendarSettingsQueryV2,
    {
      props: ({ data }) => {
        if (data?.LearnerCalendar) {
          const queryLearnerCalendar = data.LearnerCalendar?.queryLearnerCalendar;

          if (!queryLearnerCalendar) {
            return {
              data,
            };
          }

          const { url } = queryLearnerCalendar;

          return {
            data,
            url,
          };
        }

        return {};
      },
      options: () => {
        const userId = user.get()?.id?.toString();
        return {
          ...contextOption,
          variables: {
            userIdString: userId,
          },
          skip: !userId,
        };
      },
    }
  ),
  graphql<{}, CalendarMutationMutation, CalendarMutationMutationVariables, PropsFromCreateCalendarMutation>(
    CreateLearnerCalendarMutation,
    {
      name: 'createCalendarMutation',
    }
  ),
  WithCalendarData
);
